import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSupabase, useTenant } from "@how/app-kit";

function useTenantId(): string | null {
  const tenant = useTenant();
  return tenant.status === "resolved" ? tenant.tenant.tenantId : null;
}

export interface ManagedRoom {
  readonly id: string;
  readonly name: string;
  readonly campusId: string | null;
  readonly campusName: string | null;
  readonly ageMin: number | null;
  readonly ageMax: number | null;
  readonly capacity: number | null;
  readonly isActive: boolean;
  readonly sortOrder: number;
}

/** Every room of the church, active and archived, in the order the station lists them. A church has a few dozen at most. */
export function useManagedRooms() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  return useQuery({
    queryKey: ["check-in-rooms", tenantId, "all"],
    enabled: tenantId !== null,
    queryFn: async (): Promise<ManagedRoom[]> => {
      const { data, error } = await supabase
        .from("check_in_locations")
        .select(
          "id, name, campus_id, age_min, age_max, capacity, is_active, sort_order, campuses(name)",
        )
        .eq("tenant_id", tenantId ?? "")
        .order("sort_order")
        .order("name");
      if (error) throw error;
      return data.map((r) => ({
        id: r.id,
        name: r.name,
        campusId: r.campus_id,
        campusName: r.campuses?.name ?? null,
        ageMin: r.age_min,
        ageMax: r.age_max,
        capacity: r.capacity,
        isActive: r.is_active,
        sortOrder: r.sort_order,
      }));
    },
  });
}

/** What the form holds: numbers are empty strings until typed, so "no limit" is a blank box. */
export interface RoomFields {
  readonly name: string;
  readonly campusId: string | null;
  readonly ageMin: number | "";
  readonly ageMax: number | "";
  readonly capacity: number | "";
  readonly sortOrder: number;
}

export const EMPTY_ROOM: RoomFields = {
  name: "",
  campusId: null,
  ageMin: "",
  ageMax: "",
  capacity: "",
  sortOrder: 0,
};

/** Problems with the form, in words, keyed by the field they belong to. An empty object means it can be saved. */
export function validateRoom(
  fields: RoomFields,
  otherNames: readonly string[],
): Partial<Record<"name" | "ageMin" | "ageMax" | "capacity", string>> {
  const problems: Partial<Record<"name" | "ageMin" | "ageMax" | "capacity", string>> = {};
  const name = fields.name.trim();
  if (name.length === 0) problems.name = "Give the room a name.";
  else if (name.length > 80) problems.name = "Keep the name to 80 characters or fewer.";
  else if (otherNames.some((n) => n.trim().toLowerCase() === name.toLowerCase()))
    problems.name = "Another room already has that name.";
  for (const key of ["ageMin", "ageMax"] as const) {
    const value = fields[key];
    if (value !== "" && (!Number.isInteger(value) || value < 0 || value > 120))
      problems[key] = "Use a whole number of years from 0 to 120.";
  }
  if (
    problems.ageMin === undefined &&
    problems.ageMax === undefined &&
    fields.ageMin !== "" &&
    fields.ageMax !== "" &&
    fields.ageMax < fields.ageMin
  )
    problems.ageMax = "The oldest age cannot be younger than the youngest.";
  if (fields.capacity !== "" && (!Number.isInteger(fields.capacity) || fields.capacity < 1))
    problems.capacity = "Capacity is a whole number of 1 or more, or leave it empty for no limit.";
  return problems;
}

function useRoomInvalidation() {
  const client = useQueryClient();
  const tenantId = useTenantId();
  // The station's own list shares the first two key parts, so one call refreshes both.
  return () => client.invalidateQueries({ queryKey: ["check-in-rooms", tenantId] });
}

export function useSaveRoom() {
  const supabase = useSupabase();
  const tenantId = useTenantId();
  const invalidate = useRoomInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly roomId: string | null; readonly fields: RoomFields }) => {
      const f = input.fields;
      const { error } = await supabase.rpc("set_check_in_location", {
        p_tenant_id: tenantId ?? "",
        p_name: f.name.trim(),
        ...(input.roomId === null ? {} : { p_location_id: input.roomId }),
        ...(f.campusId === null ? {} : { p_campus_id: f.campusId }),
        ...(f.ageMin === "" ? {} : { p_age_min: f.ageMin }),
        ...(f.ageMax === "" ? {} : { p_age_max: f.ageMax }),
        ...(f.capacity === "" ? {} : { p_capacity: f.capacity }),
        p_sort_order: f.sortOrder,
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: invalidate,
  });
}

export function useSetRoomActive() {
  const supabase = useSupabase();
  const invalidate = useRoomInvalidation();
  return useMutation({
    mutationFn: async (input: { readonly roomId: string; readonly isActive: boolean }) => {
      const { error } = await supabase.rpc("set_check_in_location_active", {
        p_location_id: input.roomId,
        p_is_active: input.isActive,
      });
      if (error) throw Object.assign(new Error(error.message), { code: error.code });
    },
    onSuccess: invalidate,
  });
}

/** "Ages 4 to 10", "Ages 11 and up", "Up to age 3", "Any age". */
export function describeAges(min: number | null, max: number | null): string {
  if (min === null && max === null) return "Any age";
  if (min === null) return `Up to age ${String(max)}`;
  if (max === null) return `Ages ${String(min)} and up`;
  return min === max ? `Age ${String(min)}` : `Ages ${String(min)} to ${String(max)}`;
}
